import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { useNavigate, useParams } from "react-router"
import Select from "react-select"

import { GET, POST, PUT, httpOk } from "helpers/api"
import { formatMoney } from "helpers/helpers"
import HintIcon from "pages/parts/HintIcon"

function CreateUpdate() {
	const { t } = useTranslation()
	const { id } = useParams()
	const navigate = useNavigate()
	const reduxAccount = useSelector(state => state.account)

	const [salaryTypes, setSalaryTypes] = useState([])
	const [permissions, setPermissions] = useState([])
	const [activeTab, setActiveTab] = useState(null)
	const [sendData, setSendData] = useState({ name: "", pos_id: reduxAccount.pos_id })

	function returnStyle(item) {
		const style = { width: "90%", borderBottom: "1px solid var(--dark-border, #eee)" }
		if (item.parent_id) {
			style.marginLeft = "20px"
			return style
		}

		style.fontWeight = "600"
		return style
	}

	function normalizeSalaryValue(value) {
		if (value === undefined || value === null || value === "") return ""
		return value
	}

	function collectCheckedPermissions() {
		const selectedPermissions = []
		for (let i = 0; i < permissions.length; i++) {
			for (let j = 0; j < permissions[i]?.permissions?.length; j++) {
				if (permissions[i].permissions[j].checked) {
					selectedPermissions.push(permissions[i].permissions[j])
				}
			}
		}
		return selectedPermissions
	}

	function resetPermissionsUnchecked() {
		setPermissions(prev => prev.map(group => ({
			...group,
			checked: false,
			permissions: (group.permissions || []).map(permission => ({
				...permission,
				checked: false,
			})),
		})))
		setActiveTab(0)
	}

	function applyCheckedPermissions(checkedPermissions = []) {
		const checkedPermissionIds = new Set(checkedPermissions.map(item => item.id))
		const permissionsCopy = permissions.map(group => {
			const groupPermissions = (group.permissions || []).map(permission => ({
				...permission,
				checked: checkedPermissionIds.has(permission.id),
			}))

			return {
				...group,
				permissions: groupPermissions,
				checked: groupPermissions.length > 0 && groupPermissions.every(permission => permission.checked),
			}
		})

		setPermissions(permissionsCopy)
	}

	function selectAll(checked) {
		if (activeTab === null || activeTab < 0) return

		const permissionsCopy = [...permissions]
		for (let i = 0; i < permissionsCopy[activeTab]?.permissions?.length; i++) {
			permissionsCopy[activeTab].permissions[i].checked = checked
		}
		permissionsCopy[activeTab].checked = checked
		setPermissions(permissionsCopy)
	}

	function selectOne(checked, permissionId) {
		if (activeTab === null || activeTab < 0) return

		const permissionsCopy = [...permissions]
		const permission = permissionsCopy[activeTab]?.permissions?.find(item => item.id === permissionId)
		if (!permission) return

		permission.checked = checked
		permissionsCopy[activeTab].checked = permissionsCopy[activeTab].permissions.every(p => p.checked)
		setPermissions(permissionsCopy)
	}

	function renderPermissionRow(item) {
		return (
			<div
				className="d-flex justify-content-between bg-hover-grey"
				key={item.id}
				onClick={() => selectOne(!item.checked, item.id)}
			>
				<div style={returnStyle(item)} title={item.value}>
					{item.order_number}. {item.name}
				</div>
				<div className="form-check" style={{ justifyContent: "flex-end", width: "10%" }}>
					<input
						className="form-check-input"
						type="checkbox"
						checked={Boolean(item.checked)}
						onChange={(e) => {
							e.stopPropagation()
							selectOne(e.target.checked, item.id)
						}}
					/>
				</div>
			</div>
		)
	}

	function renderActiveCategoryPermissions() {
		const activeCategory = permissions[activeTab]
		if (!activeCategory) {
			return null
		}

		if (activeCategory.sections?.length > 0) {
			return activeCategory.sections.map((section, sectionIndex) => {
				const sectionPermissionIds = new Set((section.permissions || []).map(item => item.id))
				const sectionRows = (activeCategory.permissions || []).filter(item => sectionPermissionIds.has(item.id))

				return (
					<div key={section.id || section.code || sectionIndex} className={sectionIndex > 0 ? "mt-3" : ""}>
						<div
							className="fw-semibold text-muted pb-1 mb-2"
							style={{ borderBottom: "1px solid var(--dark-border, #ddd)" }}
						>
							{section.name}
						</div>
						{sectionRows.map(item => renderPermissionRow(item))}
					</div>
				)
			})
		}

		return activeCategory.permissions?.map(item => renderPermissionRow(item))
	}

	async function createUpdate(e, saveContinue = false) {
		e.preventDefault()

		const payload = {
			...sendData,
			pos_id: reduxAccount.pos_id,
			permissions: collectCheckedPermissions(),
		}

		let response
		if (payload.id) {
			response = await PUT("/employee-positions", payload, { loader: true })
		} else {
			response = await POST("/employee-positions", payload, { loader: true })
		}

		if (!httpOk(response)) return

		if (payload.id || !saveContinue) {
			navigate("/employee-positions")
			return
		}

		setSendData({ name: "", pos_id: reduxAccount.pos_id })
		resetPermissionsUnchecked()
	}

	async function getEmployeeSalaryTypes() {
		const response = await GET("/helper/employee-salary-types")
		if (httpOk(response)) setSalaryTypes(response.data)
	}

	async function getPermissions() {
		const response = await GET("/helper/permissions")
		if (httpOk(response)) {
			setPermissions(response.data)
			setActiveTab(0)
			return response.data
		}
		return []
	}

	async function getData() {
		const permissionsData = await getPermissions()

		if (!id) return

		const response = await GET(`/employee-positions/${id}`, {}, { loader: true })
		if (httpOk(response)) {
			setSendData(response.data)
			const selectedPermissions = response.data.permissions || []
			const checkedPermissionIds = new Set(selectedPermissions.map(item => item.id))
			const permissionsCopy = permissionsData.map(group => {
				const groupPermissions = (group.permissions || []).map(permission => ({
					...permission,
					checked: checkedPermissionIds.has(permission.id),
				}))

				return {
					...group,
					permissions: groupPermissions,
					checked: groupPermissions.length > 0 && groupPermissions.every(permission => permission.checked),
				}
			})
			setPermissions(permissionsCopy)
		}
	}

	useEffect(() => {
		getData()
		getEmployeeSalaryTypes()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (permissions.length > 0 && sendData.permissions) {
			applyCheckedPermissions(sendData.permissions)
		}
	}, [permissions.length]) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			<div className="page-header d-flex justify-content-between">
				<div className="d-flex text-truncate">
					<h4 className="vertical-center">
						{t("positions")}
					</h4>
					<div className="vertical-center mt-1">
						<i className="uil uil-angle-double-right fz-20"></i>
					</div>
					<div className="vertical-center mt-1">
						<h6>{id ? t("edit") : t("create")}</h6>
					</div>
				</div>
			</div>

			<div className="card">
				<div className="card-body">
					<div className="d-flex gap-3">
						<div style={{ width: "35%" }}>
							<form onSubmit={(e) => createUpdate(e, false)} autoComplete="off">
								<div className="form-group">
									<label>{t("name2")}<span className="required-mark">*</span></label>
									<input
										type="text"
										className="form-control"
										name="name"
										value={sendData.name || ""}
										onChange={(e) => setSendData({ ...sendData, name: e.target.value })}
									/>
								</div>
								<div className="form-group">
									<label>{t("choose_payment_type")}<span className="required-mark">*</span></label>
									<Select
										isClearable
										options={salaryTypes}
										value={salaryTypes.find(option => option.id === sendData.salary_type_id)}
										onChange={(option) => setSendData({ ...sendData, salary_type_id: option ? option.id : null })}
										placeholder=""
										noOptionsMessage={() => t("list_empty")}
										getOptionLabel={(option) => option.name}
										getOptionValue={(option) => option.id}
									/>
								</div>
								{(sendData.salary_type_id === 1 || sendData.salary_type_id === 2) &&
									<div className="form-group">
										<label>{t("time_rate")}<span className="required-mark">*</span></label>
										<input
											type="number"
											className="form-control"
											name="time_rate"
											value={normalizeSalaryValue(sendData.time_rate)}
											onChange={(e) => setSendData({ ...sendData, time_rate: e.target.value })}
										/>
									</div>
								}
								{sendData.salary_type_id === 3 &&
									<div className="form-group">
										<label>{t("salary")}<span className="required-mark">*</span></label>
										<input
											type="text"
											className="form-control"
											name="salary"
											value={sendData.salary ? formatMoney(sendData.salary) : ""}
											onChange={(e) => setSendData({ ...sendData, salary: e.target.value.replace(/[^0-9.]/g, "") })}
										/>
									</div>
								}
								{/* Налоги с оклада: удержания с сотрудника и налог работодателя */}
								<label>{t("salary_taxes")}<HintIcon text={t("salary_taxes_hint")} /></label>
								<div className="d-flex gap-2">
									<div className="form-group w-100">
										<label>{t("income_tax_rate")}</label>
										<input
											type="number"
											className="form-control"
											name="income_tax_rate"
											min="0"
											max="100"
											step="0.01"
											value={normalizeSalaryValue(sendData.income_tax_rate)}
											onChange={(e) => setSendData({ ...sendData, income_tax_rate: e.target.value })}
										/>
								</div>
										<div className="form-group w-100">
											<label>{t("pension_rate")}</label>
											<input
												type="number"
												className="form-control"
												name="pension_rate"
												min="0"
												max="100"
												step="0.01"
												value={normalizeSalaryValue(sendData.pension_rate)}
												onChange={(e) => setSendData({ ...sendData, pension_rate: e.target.value })}
											/>
										</div>
										<div className="form-group w-100">
											<label>{t("social_tax_rate")}</label>
											<input
												type="number"
												className="form-control"
												name="social_tax_rate"
												min="0"
												max="100"
												step="0.01"
												value={normalizeSalaryValue(sendData.social_tax_rate)}
												onChange={(e) => setSendData({ ...sendData, social_tax_rate: e.target.value })}
											/>
										</div>
								</div>
								<div className="form-group">
									<label>{t("comment")}</label>
									<textarea
										className="form-control"
										name="note"
										value={sendData.note || ""}
										onChange={(e) => setSendData({ ...sendData, note: e.target.value })}
									/>
								</div>
							</form>
						</div>
						<div style={{ width: "65%" }}>
							<div className="d-flex gap-2">
								<div className="d-flex flex-column" style={{ width: "30%" }}>
									{permissions.map((item, index) => (
										<div
											key={index}
											className="cursor"
											style={activeTab === index ? { borderBottom: "1px solid #000" } : {}}
											onClick={() => setActiveTab(index)}
										>
											<span className="link">{item.name}</span>
										</div>
									))}
								</div>
								<div style={{ width: "70%" }}>
									{activeTab >= 0 &&
										<div style={{ height: "600px", overflow: "auto" }}>
											{permissions.map((item, index) => (
												index === activeTab &&
												<div className="d-flex justify-content-between" key={index}>
													<h4>{t("all")}</h4>
													<div className="form-check">
														<input
															className="form-check-input"
															type="checkbox"
															checked={!!item.checked}
															onChange={(e) => selectAll(e.target.checked)}
														/>
													</div>
												</div>
											))}

											{renderActiveCategoryPermissions()}
										</div>
									}
								</div>
							</div>
						</div>
					</div>
					<div className="btn-wrapper gap-2 mt-5">
						<button type="button" className="btn btn-warning btn-rounded btn-wide"
							onClick={() => navigate(-1)}>
							{t("cancel")}
						</button>
						{!id &&
							<button type="button" className="btn btn-secondary btn-rounded btn-wide"
								onClick={(e) => createUpdate(e, true)}>
								{t("save")}
								<i className="uil uil-arrow-right"></i>
							</button>
						}
						<button type="button" className="btn btn-primary btn-rounded btn-wide"
							onClick={(e) => createUpdate(e, false)}>
							{t("save")}
						</button>
					</div>
				</div>
			</div>
		</>
	)
}

export default CreateUpdate
